/**
 * אנשי חוץ — the offline store behind the directory, on Dexie version 13.
 *
 * The whole point of this module is what it does *not* touch. A person kept
 * here is written to `externalPeople` and to nothing else: never to `soldiers`,
 * so the מצבה, the availability counters and the workload graph keep counting
 * the unit's own people; never to `vehicles`, so a contractor's van is not a
 * fleet record with a מספר פנימי and an availability light of its own. That
 * separation is the requirement, and {@link countsAgainstRosters} is the
 * assertion of it.
 *
 * Reads are open — a manager filling a team form needs the list. Writes need
 * `ASSIGN_TASK`, the same permission the assignment dialog itself is gated by,
 * because keeping a card is part of crewing a mission and nothing else.
 *
 * The rules are not re-implemented here: `src/core/external-people.ts` decides
 * what a card looks like and when two entries are the same person.
 */
import { db } from './index';
import { assertPermission } from '../core/permissions';
import {
  findExternalPersonMatch,
  isKeepableExternalPerson,
  normalizeExternalPersonRecord,
  sortExternalPeople,
  toExternalPersonDraft,
} from '../core/external-people';
import { emitStoreChange } from '../core/store-events';
import type { CrewRole, ExternalPerson, ExternalPersonDraft } from '../types/assignment';
import type { ExternalPersonRecord, ExternalPersonRecordDraft } from '../types/external-person';
import type { User } from '../types/user';

// ── Reads ────────────────────────────────────────────────────────────

/** The whole directory, most recently placed first. */
export async function getExternalPeople(): Promise<ExternalPersonRecord[]> {
  return sortExternalPeople(await db.externalPeople.toArray());
}

/** One kept card, or `null` when it is unknown. */
export async function getExternalPerson(id: string): Promise<ExternalPersonRecord | null> {
  if (!id) return null;
  return (await db.externalPeople.get(id)) ?? null;
}

/** How many people the directory holds — the settings screen's counter. */
export async function countExternalPeople(): Promise<number> {
  return db.externalPeople.count();
}

/**
 * The card this person already has, if any. Asked before a write, so the second
 * run of the same contractor updates one card instead of opening a second.
 */
export async function findExternalPerson(
  person: Partial<ExternalPerson> | null | undefined,
): Promise<ExternalPersonRecord | null> {
  return findExternalPersonMatch(await db.externalPeople.toArray(), person);
}

// ── Writes ───────────────────────────────────────────────────────────

/**
 * Keeps one person in the directory, or updates the card they already have.
 *
 * Returns `null` for a draft with no name — "שמור לפעמים הבאות" ticked over an
 * empty form is a mis-tap, not a request to file a blank card.
 */
export async function saveExternalPerson(
  actor: User,
  draft: ExternalPersonRecordDraft,
  now: number = Date.now(),
): Promise<ExternalPersonRecord | null> {
  assertPermission(actor, 'ASSIGN_TASK');
  if (!isKeepableExternalPerson(draft)) return null;

  const existing = await findExternalPerson(draft);
  const record = normalizeExternalPersonRecord(draft, { existing, now });
  await db.externalPeople.put(record);
  emitStoreChange('assignments');
  return record;
}

/**
 * Keeps somebody who was just placed on a mission. The convenience the team
 * form actually calls: it is handed the rider it built and the seat it put them
 * in, and the card is derived from those.
 */
export async function rememberExternalPerson(
  actor: User,
  person: ExternalPerson | ExternalPersonDraft,
  crewRole?: CrewRole,
  now: number = Date.now(),
): Promise<ExternalPersonRecord | null> {
  return saveExternalPerson(actor, toExternalPersonDraft(person, crewRole), now);
}

/** Drops one card. The missions they already rode on are untouched. */
export async function deleteExternalPerson(actor: User, id: string): Promise<boolean> {
  assertPermission(actor, 'ASSIGN_TASK');
  if (!(await db.externalPeople.get(id))) return false;
  await db.externalPeople.delete(id);
  emitStoreChange('assignments');
  return true;
}

// ── The separation, asserted ─────────────────────────────────────────

/**
 * How many of the directory's people leaked into the two rosters they are
 * deliberately kept out of. Always `0` — and it is checked rather than assumed,
 * because "אנשי חוץ do not load the מצבה" is the requirement, not a side effect.
 */
export async function countsAgainstRosters(): Promise<{ soldiers: number; vehicles: number }> {
  const people = await db.externalPeople.toArray();
  if (people.length === 0) return { soldiers: 0, vehicles: 0 };

  const ids = new Set(people.map((person) => person.id));
  const plates = new Set(
    people.map((person) => String(person.plateNumber ?? '').trim()).filter(Boolean),
  );
  const [soldiers, vehicles] = await Promise.all([
    db.soldiers.toArray(),
    db.vehicles.toArray(),
  ]);

  return {
    soldiers: soldiers.filter((soldier) => ids.has(soldier.id)).length,
    vehicles: vehicles.filter(
      (vehicle) => ids.has(vehicle.id) || plates.has(String(vehicle.plateNumber ?? '').trim()),
    ).length,
  };
}

// ── Maintenance ──────────────────────────────────────────────────────

/** Wipes the directory — used by the "reset local data" action. */
export async function resetExternalPeople(): Promise<void> {
  await db.externalPeople.clear();
  emitStoreChange('assignments');
}
